import { Controller } from 'react-hook-form';
import { Option } from '@/legacy/types';
import { Select } from '@/legacy/components/ui/Form/Select/primitives';
import { useTranslation, Trans } from 'react-i18next';
import { formPlaceholderTradPrefix } from '@/legacy/i18n';
import type { CheckboxShape } from '@frekuent/orbit/components';

export type MultiSelectProps = {
	options: Option[];
	isLoadingOptions?: boolean;
	name: string;
	placeholder?: string;
	label?: string;
	tooltipId?: string;
	tooltipText?: string;
	required?: boolean;
	searchable?: boolean;
	pillsVariant?: boolean;
	checkboxShape?: CheckboxShape;
	showScrollbar?: boolean;
	useAllSentinel?: boolean;
	selectAllRow?: boolean;
	disabled?: boolean;
	autoPlacement?: boolean;
	infoCopy?: React.ReactNode;
	reorderSelectedToTop?: boolean;
	helperText?: string | null;
	tradPrefixSelectedHelperText?: string | null;
	spacing?: boolean;
};

const MultiSelect = ({
	options,
	isLoadingOptions,
	name,
	placeholder = `${formPlaceholderTradPrefix}.select_options`,
	label,
	tooltipId,
	tooltipText,
	required,
	searchable = true,
	pillsVariant = false,
	checkboxShape = 'square',
	showScrollbar = true,
	useAllSentinel = false,
	disabled = false,
	selectAllRow = true,
	autoPlacement = true,
	infoCopy,
	reorderSelectedToTop = true,
	helperText,
	tradPrefixSelectedHelperText,
	spacing = true,
}: MultiSelectProps) => {
	const { t } = useTranslation();
	return (
		<Controller
			name={name}
			render={({ field, fieldState: { error: fieldError } }) => {
				const errorMessage = Array.isArray(fieldError)
					? fieldError.find((error) => error)?.message
					: fieldError?.message;

				return (
					<Select.Root
						options={options}
						value={field.value}
						onChange={field.onChange}
						floatingUiProps={{
							strategy: 'fixed',
							autoplacement: autoPlacement,
						}}
						multiple
						pillsVariant={pillsVariant}
						selectAllRow={selectAllRow}
						useAllSentinel={useAllSentinel}
						reorderSelectedToTop={reorderSelectedToTop}
					>
						<div className={spacing ? 'mb-6' : ''}>
							{label && (
								<Select.Label
									label={label}
									name={name}
									tooltipId={tooltipId}
									tooltipText={tooltipText}
									required={required}
									children={
										field.value?.length > 0 &&
										!pillsVariant && (
											<span>
												{t(`${formPlaceholderTradPrefix}.selected_options`, {
													count: field.value?.length || 0,
												})}
											</span>
										)
									}
									infoCopy={infoCopy}
								/>
							)}
							<Select.Trigger
								placeholder={t(placeholder)}
								fieldError={errorMessage}
								variant='button'
								disabled={disabled}
							/>

							<Select.HelperText error={errorMessage}>
								{field.value?.length > 0 && tradPrefixSelectedHelperText ? (
									<Trans
										i18nKey={tradPrefixSelectedHelperText}
										values={{ count: field.value?.length }}
										components={{ b: <b /> }}
									/>
								) : (
									helperText
								)}
							</Select.HelperText>
							<Select.Positioner className='w-full'>
								<Select.Panel isLoadingOptions={isLoadingOptions}>
									<Select.OptionsList
										searchable={searchable}
										checkBoxShape={checkboxShape}
										isLoadingOptions={isLoadingOptions}
										showScrollbar={showScrollbar}
									/>
								</Select.Panel>
							</Select.Positioner>
						</div>
					</Select.Root>
				);
			}}
		/>
	);
};

export default MultiSelect;
